import { createContext, useContext, useEffect, useState } from 'react';
import { useListState } from '@mantine/hooks';
import { useAuth } from '@/contexts/auth.ctx.tsx';
import type { Profile } from '@prisma/client';
type ProfilePartial = Partial<Profile>;
import type { TestMetaProfile } from '@/testmeta.types';
import { DateTime } from 'luxon';

interface ProfileContextType {
    profiles: Profile[] | null;
    selectedProfile: Profile | null;
    profileCount: number;
    daysSinceLastTest: number;
    testMetaProfile: TestMetaProfile | null;
    updateProfile: (profile: ProfilePartial) => Promise<void>;
    updateTestMetaProfile: (testMetaProfile: TestMetaProfile) => Promise<void>;
    /**
     * Creates a new profile and returns the profile ID
     * @param profile - The profile to create
     * @returns The profile ID
     */
    createProfile: (profile: ProfilePartial) => Promise<Profile>;
    deleteProfile: (profileId: string) => Promise<void>;
    setProfileImg: (profileId: string, img: File) => Promise<void>;
    testCheck: (profileId: string) => Promise<Profile>;
    setProfileByID: (profileId: string | null) => Profile | null;
    profileOptionData: {value: string, label: string}[];
}

const ProfileContext = createContext<ProfileContextType | null>(null);

export const ProfileProvider = ({ children }: { children: React.ReactNode }) => {
    const [ profiles, mod ] = useListState<Profile>([]);
    const [ selectedProfile, setSelectedProfile ] = useState<Profile | null>(null);
    const { accessor, injectHeader } = useAuth();
    const [ profileOptionData, setProfileOptionData ] = useState<{value: string, label: string}[]>([]);
    const [ profileCount, setProfileCount ] = useState<number>(0);
    const [ daysSinceLastTest, setDaysSinceLastTest ] = useState<number>(0);
    const [ testMetaProfile, setTestMetaProfile ] = useState<TestMetaProfile | null>(null);


    useEffect(() => {
        if (Object.keys(accessor || {}).length > 0) {
            (async () => {
                const req = new Request('/api/auth/profile', {
                    method: 'GET'
                });
                injectHeader(req);
                const res = await fetch(req);
                const data = await res.json() as { profiles: Profile[] };
                console.log('profiles', data.profiles);
                mod.setState(data.profiles);
                setProfileCount(data.profiles.length);
                if(data.profiles.length > 0) {
                    const cP = data.profiles[0];
                    setSelectedProfile(cP);
                    setTestMetaProfile(cP.testControlMeta as TestMetaProfile || {});
                    setProfileOptionData(data.profiles.map(p => ({value: p.id, label: p.name})));
                    if(cP.testTaken) {
                        console.log('latest', cP.testTaken);
                        // @ts-ignore
                        const ltr = DateTime.fromISO(cP.testTaken);
                        const now = DateTime.now();
                        const diff = Math.floor(now.diff(ltr, 'days').days);
                        console.log('diff', diff);
                        setDaysSinceLastTest(diff);
                    } else {
                        setDaysSinceLastTest(-1);
                    }
                } else {
                    setProfileOptionData([]);
                    setSelectedProfile(null);
                    setTestMetaProfile(null);
                }
            })()
        }
    }, [accessor])

    const updateProfile = async (profile: ProfilePartial) => {
        
        const id = !!profile.id ? profile.id : selectedProfile?.id;
        if(!id) throw new Error('Profile ID is required');
        const req = new Request(`/api/auth/profile`,
            { method: 'PUT', body: JSON.stringify({...profile, id}) }
        );
        injectHeader(req);
        const res = await fetch(req);
        if(res.ok){
            mod.applyWhere(p => p.id === id, p => ({ ...p, ...profile }));
            if(id === selectedProfile?.id) {
                setSelectedProfile(pre => ({...pre, ...profile, id} as Profile));
            }
        } else {
            throw new Error('Failed to update profile');
        }
    };

    const updateTestMetaProfile = async (testMetaProfile: TestMetaProfile) => {
        // TODO: Need to make sure this remains serializable JSON
        console.log('updateTestMetaProfile', testMetaProfile);
        const pp:ProfilePartial = {
            testControlMeta: testMetaProfile
        }
        console.log('update TestMetaProfile with', pp);
        return await updateProfile(pp);
    }

    const createProfile = async (profile: ProfilePartial) => {
        const req = new Request(`/api/auth/profile`,
            { method: 'POST', body: JSON.stringify(profile) }
        );
        injectHeader(req);
        const res = await fetch(req);
        const data = await res.json() as { profile: Profile };
        mod.append(data.profile);
        setSelectedProfile(data.profile);
        setProfileOptionData(pre => [...pre, {value: data.profile.id, label: data.profile.name}]);
        return data.profile;
    };
    const deleteProfile = async (profileId: string) => { };
    const setProfileImg = async (profileId: string, img: File) => { };

    const testCheck = async (profileId: string) => {
        const req = new Request(`/api/auth/profile?testCheck=true&profileID=${profileId}`, {
            method: 'GET'
        });
        injectHeader(req);
        const res = await fetch(req);
        const data = await res.json() as { profile: Profile };
        mod.applyWhere(p => p.id === profileId, p => ({ ...p, ...data.profile }));
        if(profileId === selectedProfile?.id) {
            setSelectedProfile(pre => ({...pre, ...data.profile, id: profileId} as Profile));
        }
        return data.profile;
    }

    const setProfileByID = (profileId: string | null) => {
        if(profileId === null){
            setSelectedProfile(null);
            return null; // we have to be explicit null here
        }
        const profile = profiles.find(p => p.id === profileId);
        if(profile) {
            setSelectedProfile(profile);
        }
        return !!profile ? profile : null; // we have to be explicit null here
    }

    return (
        <ProfileContext.Provider value={{ profiles, updateProfile, daysSinceLastTest, createProfile, deleteProfile, setProfileImg, testCheck, selectedProfile, setProfileByID, profileOptionData, profileCount, testMetaProfile, updateTestMetaProfile }}>
            {children}
        </ProfileContext.Provider>
    );
};

export const useProfile = () => {
    const context = useContext(ProfileContext);
    if (!context) {
        throw new Error('useProfile must be used within a ProfileProvider');
    }
    return context;
};